import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { Divider, Stack } from '@mui/material';
import DataDisplay from '@sharedCR/coreDesignSystem/DataDisplay';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { SellerAdjustmentType } from 'constants/loanApplication';
import { PendingChangesActionType } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import useIsMounted from 'hooks/useIsMounted';
import { getPendingChangesInfo } from 'utils/loanFees';

import { showMessageAction } from 'store/global/actions';
import {
  editLoanSellerAdjustmentsAction,
  getLoanFeesSummary,
  getLoanFeesViewAction,
} from 'store/loanApplications/actions';
import { selectLoanFeesSectionKEdit } from 'store/loanApplications/selectors';
import { getSellerAdjustmentTypesAction } from 'store/system/actions';
import {
  selectFeeProviderOptions,
  selectFeeTypesOptionsWithoutSellerPaid,
} from 'store/system/selectors';

import PendingChangesNumber from '../../../Label/PendingChangesNumber';
import TotalAmount from '../../../Label/TotalAmount';
import AdjustmentsTable from '../../AdjustmentsTable';
import { ADJUSTMENTS_FORM_DATASET_NAME } from '../../AdjustmentsTable/AdjustmentsTable.constants';
import Controls from '../../Controls';
import useTriggerAddIfRequested from '../../hooks/useTriggerAddIfRequested';
import {
  getAdjustmentsForUpdate,
  getFormAdjustmentsForUpdate,
  getInitialAdjustmentsFormValues,
  getRowsWithLocallyAdded,
  getSchemaAdjustments,
} from '../Section.utils';

import { sectionContentStyles } from './SectionK.styles';

const schema = object().shape({
  [ADJUSTMENTS_FORM_DATASET_NAME]: getSchemaAdjustments(),
});

const SectionK = ({
  cdSection,
  isWithHeader,
  items: viewItems,
  labelNameAmountSection,
  isLoading,
  setEditedSection,
  setIsLoading,
  parentRef,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const [{ pendingChangesNumber, isTotalAmountPending }, setPendingChangesInfo] = useState({});

  const isMounted = useIsMounted();

  const sectionKEditData = useSelector(selectLoanFeesSectionKEdit);

  const feesOptions = useSelector(selectFeeTypesOptionsWithoutSellerPaid);
  const feeProviderOptions = useSelector(selectFeeProviderOptions);

  const {
    adjustmentsPaidBySeller,
    adjustmentsUnpaidBySeller,
    adjustmentTypeOptions,
    adjustmentTypeOptionsNotDeleted,
    tableTitle,
    dataDisplayItems,
    constantTotalAmount,
  } = sectionKEditData;

  const {
    form,
    setForm,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    validateField,
    isFormChanged,
    validateForm,
    setIsTouched,
  } = useForm({
    [ADJUSTMENTS_FORM_DATASET_NAME]: getInitialAdjustmentsFormValues({
      adjustments: adjustmentsPaidBySeller,
      adjustmentTypeOptions,
    }),
  });

  useEffectSafeStrictMode(() => {
    setIsLoading(true);

    dispatch(getSellerAdjustmentTypesAction({ includeDeleted: true }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, applicationId]);

  useEffect(() => {
    const initialAdjustments = getInitialAdjustmentsFormValues({
      adjustments: adjustmentsPaidBySeller,
      adjustmentTypeOptions,
    });

    setForm(prev => ({
      [ADJUSTMENTS_FORM_DATASET_NAME]: getRowsWithLocallyAdded({
        initialRows: initialAdjustments,
        currentRows: prev[ADJUSTMENTS_FORM_DATASET_NAME],
      }),
    }));
  }, [
    viewItems,
    setForm,
    feesOptions,
    feeProviderOptions,
    adjustmentsPaidBySeller,
    adjustmentTypeOptions,
    adjustmentTypeOptionsNotDeleted,
  ]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    const pendingChangesInfo = getPendingChangesInfo(parentRef);
    setPendingChangesInfo(pendingChangesInfo);
  }, [form, parentRef]);

  useEffect(() => {
    setEditedSection(prev => ({ ...prev, isFormChanged, ref: parentRef }));
  }, [setEditedSection, isFormChanged, parentRef]);

  const totalAmount = useMemo(() => {
    return (
      constantTotalAmount +
      _.sumBy(form[ADJUSTMENTS_FORM_DATASET_NAME], adjustment =>
        adjustment?.pendingChangesActionType !== PendingChangesActionType.DELETED
          ? Number(adjustment.totalAmount || 0)
          : 0,
      )
    );
  }, [form, constantTotalAmount]);

  const onAdd = useCallback(() => {
    setIsTouched(true);

    setForm(prev => ({
      ...prev,
      [ADJUSTMENTS_FORM_DATASET_NAME]: [
        ...prev[ADJUSTMENTS_FORM_DATASET_NAME],
        {
          isLocallyAdded: true,
          adjustmentId: crypto.randomUUID(),
          pendingChangesActionType: PendingChangesActionType.ADDED,
        },
      ],
    }));
  }, [setForm, setIsTouched]);

  const onDelete = useCallback(
    e => {
      const { adjustmentId } = e.currentTarget.dataset;

      setIsTouched(true);

      setForm(prev => ({
        ...prev,
        [ADJUSTMENTS_FORM_DATASET_NAME]: prev[ADJUSTMENTS_FORM_DATASET_NAME].reduce(
          (acc, adjustment) => {
            if (adjustment.adjustmentId === adjustmentId && adjustment.isLocallyAdded) {
              return acc;
            }

            acc.push(
              adjustment.adjustmentId === adjustmentId
                ? {
                    ...adjustment,
                    isDeleted: true,
                    pendingChangesActionType: PendingChangesActionType.DELETED,
                  }
                : adjustment,
            );

            return acc;
          },
          [],
        ),
      }));
    },
    [setForm, setIsTouched],
  );

  const onCancelForm = useCallback(() => {
    setEditedSection(null);
  }, [setEditedSection]);

  const onSubmit = useCallback(
    async e => {
      e?.preventDefault?.();

      if (!(await validateForm())) {
        return;
      }

      const adjustmentsForSend = [
        ...getFormAdjustmentsForUpdate({
          formAdjustments: form[ADJUSTMENTS_FORM_DATASET_NAME],
          adjustmentType: SellerAdjustmentType.PAID,
        }),
        ...getAdjustmentsForUpdate(adjustmentsUnpaidBySeller),
      ];

      setIsLoading(true);
      dispatch(
        editLoanSellerAdjustmentsAction({
          sellerAdjustments: adjustmentsForSend,
          applicationId,
          requestMetadata: { tabId: LoanApplicationTabId.LOAN_FEES },
        }),
      )
        .then(() => {
          dispatch(getLoanFeesViewAction({ applicationId }));
          dispatch(getLoanFeesSummary({ applicationId }));
        })
        .then(() => {
          if (isMounted) {
            setIsTouched(false);
          }

          setEditedSection(null);
        })
        .then(() => {
          dispatch(showMessageAction({ message: 'Successfully edited', variant: 'success' }));
        })
        .catch(({ response }) => handleLoanFeesError({ response, dispatch }))
        .finally(() => {
          setIsLoading(false);
        });
    },
    [
      adjustmentsUnpaidBySeller,
      applicationId,
      dispatch,
      form,
      isMounted,
      setEditedSection,
      setIsLoading,
      setIsTouched,
      validateForm,
    ],
  );

  useEffect(() => {
    parentRef.current.onCancelForm = onCancelForm;
    parentRef.current.onSubmit = onSubmit;
  }, [onCancelForm, onSubmit, parentRef]);

  useTriggerAddIfRequested(setEditedSection, onAdd);

  const meta = useMemo(
    () => ({
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
    }),
    [
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setEditedSection,
    ],
  );

  return (
    <>
      <Stack sx={sectionContentStyles}>
        <Stack className="data-panel">
          {dataDisplayItems.map(item => (
            <DataDisplay
              className="data-display"
              key={item.id}
              title={item.name}
              value={item.amount}
            />
          ))}
        </Stack>

        <Divider />

        <AdjustmentsTable
          adjustmentTypeOptions={adjustmentTypeOptions}
          adjustmentTypeOptionsNotDeleted={adjustmentTypeOptionsNotDeleted}
          data={form[ADJUSTMENTS_FORM_DATASET_NAME]}
          isLoading={isLoading}
          isWithHeader={isWithHeader}
          labelNameAmountSection={labelNameAmountSection}
          meta={meta}
          setIsLoading={setIsLoading}
          tableTitle={tableTitle}
          onAdd={onAdd}
          onDelete={onDelete}
        />
      </Stack>

      <Controls
        isFormChanged={isFormChanged}
        isLoading={isLoading}
        onCancelForm={onCancelForm}
        onSubmit={onSubmit}
      />
      {createPortal(
        <TotalAmount isPending={isTotalAmountPending} value={totalAmount} />,
        document.getElementById(`total-loan-fee-section-${cdSection}`),
      )}
      {createPortal(
        <PendingChangesNumber value={pendingChangesNumber} />,
        document.getElementById(`pending-changes-number-${cdSection}`),
      )}
    </>
  );
};

SectionK.propTypes = {
  ...WithLoadingPropTypes,
  setIsLoading: PropTypes.func.isRequired,
  isWithHeader: PropTypes.bool.isRequired,
  cdSection: PropTypes.string.isRequired,
  items: PropTypes.array.isRequired,
  setEditedSection: PropTypes.func.isRequired,
  parentRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  labelNameAmountSection: PropTypes.string,
};

export default memo(SectionK);
